import {
  type RiskGapModel,
  riskGapThumbsDownRatingReasons,
  riskGapThumbsUpRatingReasons,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { Button } from '@ctw/shared/components/button';
import { FeedbackThumbs, type ThumbStatus } from '@ctw/shared/components/feedback-thumbs';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { TagCheckboxGroup } from '@ctw/shared/components/inputs/tag-checkbox-group';
import { notifyFromDrawer } from '@ctw/shared/components/toast';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  AI_API_VERSION_V2,
  getAiServiceBaseUrl,
  getStandaloneBaseUrl,
} from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

export interface RiskGapFeedbackCardProps {
  riskGap: RiskGapModel;
}

export const RiskGapFeedbackCard = ({ riskGap }: RiskGapFeedbackCardProps) => {
  const shouldUseV3FeedbackApi = useFeatureFlag('ctw-risk-gaps-feedback-api-v3');
  const { requestContext, ctwFetch } = useCTW();
  const telemetry = useTelemetry();
  const { patient } = usePatientContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState<ThumbStatus | undefined>(undefined);
  const [feedbackReasons, setFeedbackReasons] = useState<Array<string>>([]);
  const [feedbackNotes, setFeedbackNotes] = useState<string>('');
  const [feedbackOptInToOutreach, setFeedbackOptInToOutreach] = useState<boolean>(false);

  const errorMessage =
    'Your feedback on this Risk Gap could not be submitted. Please try again and contact support if this error persists.';

  const resetFeedbackForm = useCallback(() => {
    setFeedbackValue(undefined);
    setFeedbackReasons([]);
    setFeedbackNotes('');
    setFeedbackOptInToOutreach(false);
  }, []);

  const submitButtonDisabled = useMemo(
    () => isSubmitting || feedbackValue === undefined,
    [isSubmitting, feedbackValue],
  );

  const submitFeedback = useCallback(() => {
    setIsSubmitting(true);
    const fetchPromise = shouldUseV3FeedbackApi
      ? ctwFetch(`${getStandaloneBaseUrl(requestContext.env)}/api/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
          },
          body: JSON.stringify({
            data: {
              type: 'feedback-submission',
              attributes: {
                resourceType: riskGap.resourceType,
                resourceId: riskGap.id,
                polarity: feedbackValue === true ? 'positive' : 'negative',
                reasons: feedbackReasons,
                notes: isEmpty(feedbackNotes.trim()) ? undefined : feedbackNotes,
                optedInToOutreach: feedbackOptInToOutreach,
              },
            },
          }),
        })
      : ctwFetch(
          `${getAiServiceBaseUrl({ env: requestContext.env, version: AI_API_VERSION_V2 })}/feedback`,
          {
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify({
              createdAt: new Date().toISOString(),
              userId: requestContext.authTokenState.email,
              upid: patient.UPID,
              thumbsUp: feedbackValue,
              reasons: feedbackReasons || [],
              content: feedbackNotes.trim() === '' ? 'N/A' : feedbackNotes,
              contactPermission: feedbackOptInToOutreach,
              citations: [
                {
                  id: 0,
                  source_resource_type: 'MeasureReport',
                  source_id: riskGap.id,
                },
              ],
            }),
          },
        );

    fetchPromise
      .then((response) => {
        if (response.ok) {
          notifyFromDrawer({
            type: 'success',
            title: 'Thank you! Your feedback on this Risk Gap has been submitted.',
          });
          resetFeedbackForm();
        } else {
          telemetry.trackError({
            error: new Error('Failed to submit Risk Gap feedback'),
            context: {
              data: JSON.stringify(response.data),
              headers: JSON.stringify(response.headers),
            },
          });
          notifyFromDrawer({
            type: 'error',
            title: errorMessage,
          });
        }
      })
      .catch((error) => {
        telemetry.trackError(error);
        notifyFromDrawer({
          type: 'error',
          title: errorMessage,
        });
      })
      .finally(() => setIsSubmitting(false));
  }, [
    ctwFetch,
    requestContext,
    telemetry,
    riskGap,
    patient,
    feedbackNotes,
    feedbackValue,
    feedbackReasons,
    feedbackOptInToOutreach,
    resetFeedbackForm,
    shouldUseV3FeedbackApi,
  ]);

  return (
    <section
      className={tw`group/card flex flex-col overflow-visible rounded-md border border-border-main bg-white shadow-sm`}
    >
      <header className={tw`flex items-center justify-start px-3 py-2`}>
        <div
          className={tw`overflow-hidden text-ellipsis whitespace-nowrap font-medium text-content-subtle text-sm uppercase leading-none`}
        >
          Feedback
        </div>
      </header>
      <div
        className={tw`flex flex-col justify-between gap-3 px-3 pb-2 font-normal text-content-main text-sm`}
      >
        <div className={tw`font-medium text-content-subtle text-lg leading-7`}>
          What did you think of this Risk Gap?
        </div>
        <FeedbackThumbs
          initialValue={feedbackValue}
          onChange={(value) => {
            setFeedbackValue(value);
            setFeedbackReasons([]);
            setFeedbackNotes('');
          }}
        />
        {feedbackValue !== undefined && (
          <div className={tw`flex flex-col gap-5 pt-4`}>
            <TagCheckboxGroup
              legend="Why did you choose this rating?"
              name="risk-gap-rating-reason"
              options={
                feedbackValue ? riskGapThumbsUpRatingReasons : riskGapThumbsDownRatingReasons
              }
              selectedOptions={feedbackReasons ?? []}
              onOptionSelected={(option) => {
                setFeedbackReasons((prev) => {
                  if (!prev.includes(option.value)) {
                    return [...prev, option.value];
                  }
                  return prev;
                });
              }}
              onOptionDeselected={(option) => {
                setFeedbackReasons((prev) => {
                  if (prev?.includes(option.value)) {
                    return prev.filter((reason) => reason !== option.value);
                  }
                  return prev;
                });
              }}
            />
            <label>
              <span
                className={tw`mb-1 block font-medium text-content-subtle text-xs uppercase tracking-wide`}
              >
                Is there any other feedback you would like to share?
              </span>
              <textarea
                onChange={(event) => setFeedbackNotes(event.currentTarget.value)}
                value={feedbackNotes}
                rows={4}
                className={tw`w-full`}
              />
            </label>
            <Checkbox
              checked={feedbackOptInToOutreach}
              onChange={(value) => setFeedbackOptInToOutreach(value)}
              name="feedback-opt-in-to-outreach"
              label="It's okay to reach out to me about my responses"
              className={tw`font-medium`}
            />
            <div className={tw`flex items-center justify-start gap-3`}>
              <Button
                disabled={submitButtonDisabled}
                type="button"
                variant="primary"
                onClick={() => submitFeedback()}
              >
                Send Feedback
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
